import {
  isPaidStatus,
  matterAmountOwed,
  matterAmountPaid,
  matterBalanceRemaining,
  roundMoney,
} from './paidBeforeAssignment.js';
import { formatMatterMoney } from './matterCurrency.js';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function parseDate(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

/** Whole calendar days between two timestamps (floor). */
export function daysBetween(from, to = new Date()) {
  const start = parseDate(from);
  const end = parseDate(to);
  if (!start || !end) return null;
  const diff = end.getTime() - start.getTime();
  if (diff < 0) return 0;
  return Math.floor(diff / MS_PER_DAY);
}

/**
 * Aging anchor for a demand:
 * Prefer explicit due/invoice dates when present; otherwise demand Created_At.
 */
export function matterAgingAnchor(matter = {}, { paymentTerms = [] } = {}) {
  const due =
    matter.Due_Date ||
    matter.dueDate ||
    matter.Invoice_Due_Date ||
    matter.invoiceDueDate ||
    null;
  if (due) return parseDate(due);

  const invoiceDate =
    matter.Invoice_Date || matter.invoiceDate || matter.Service_Date || matter.serviceDate || null;
  const termId = String(matter.Payment_Term_ID || matter.paymentTermId || '').trim();
  const term = termId
    ? (paymentTerms || []).find((item) => String(item.id || item.ROWID) === termId)
    : null;
  const daysDue = Number(term?.daysDue ?? term?.Days_Due);
  if (invoiceDate && Number.isFinite(daysDue) && daysDue >= 0) {
    const base = parseDate(invoiceDate);
    if (base) {
      return new Date(base.getTime() + daysDue * MS_PER_DAY);
    }
  }

  return parseDate(matter.Created_At || matter.createdAt);
}

/**
 * Collection KPIs for an existing demand record.
 * Aging is days past the due/open anchor while a balance remains;
 * when paid in full, aging freezes at the paid date (or now if unknown).
 */
export function buildMatterKpis(matter = {}, options = {}) {
  const owed = matterAmountOwed(matter);
  const paid = matterAmountPaid(matter);
  const remaining = matterBalanceRemaining({
    ...matter,
    Amount_Owed: owed,
    Amount_Paid: paid,
  });
  const paidPct = owed > 0 ? Math.round((paid / owed) * 100) : 0;
  const remainingPct = owed > 0 ? Math.max(0, 100 - paidPct) : 0;

  const paidFully = isPaidStatus(matter.Status) || remaining <= 0;
  const anchor = matterAgingAnchor(matter, options);
  const endAt = paidFully
    ? parseDate(matter.Paid_At || matter.paidAt || matter.Updated_At) || new Date()
    : new Date();
  const agingDays = daysBetween(anchor, endAt);

  let agingLabel = '—';
  if (agingDays == null) {
    agingLabel = 'No date';
  } else if (paidFully) {
    agingLabel = 'Paid';
  } else if (agingDays <= 0) {
    agingLabel = 'Current';
  } else {
    agingLabel = 'Past due';
  }

  return {
    owed: roundMoney(owed),
    paid: roundMoney(paid),
    remaining: roundMoney(remaining),
    paidPct,
    remainingPct,
    agingDays,
    agingLabel,
    paidFully,
    anchorAt: anchor ? anchor.toISOString() : null,
  };
}

export function formatKpiMoney(value, currencyOrMatter = 'USD') {
  return formatMatterMoney(value, currencyOrMatter);
}

export function formatAgingDays(days) {
  if (days == null || !Number.isFinite(days)) return '—';
  return `${Math.max(0, Math.floor(days))}d`;
}
